import { useAuth0 } from "@auth0/auth0-react";
import { createFileRoute, Outlet, useLocation } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import { FullPageLoader } from "@/components/full-page-loader";
import { AnalyticsTenantSync } from "@/features/tenant/components/analytics-tenant-sync";
import { getPrimaryRole } from "@/lib/admin-roles";
import {
  identify,
  maybeStartSessionRecording,
  registerSuperProperties,
  setPeoplePropertiesOnce,
  track,
} from "@/lib/analytics";
import { extractRoles, isTravelCustomer } from "@/lib/permissions";
import { setAuthTokenGetter, setLoginRedirector } from "@/services/api/auth-token";

export const Route = createFileRoute("/_authed")({
  component: AuthedLayout,
});

function AuthedLayout() {
  const { isAuthenticated, isLoading, error, loginWithRedirect, getAccessTokenSilently, user } = useAuth0();
  const location = useLocation();
  const identified = useRef(false);

  useEffect(() => {
    setAuthTokenGetter(() => getAccessTokenSilently());
  }, [getAccessTokenSilently]);

  useEffect(() => {
    setLoginRedirector(() => void loginWithRedirect({ appState: { returnTo: location.href } }));
  }, [loginWithRedirect, location.href]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !error) {
      void loginWithRedirect({
        appState: { returnTo: location.href },
      });
    }
  }, [isLoading, isAuthenticated, error, loginWithRedirect, location.href]);

  useEffect(() => {
    if (isAuthenticated && user?.sub && !identified.current) {
      identified.current = true;
      const roles = extractRoles(user);
      const customer = isTravelCustomer(roles);
      const traits = {
        role: getPrimaryRole(roles),
        roles,
        userType: customer ? "customer" : "internal",
      };
      identify(user.sub, { $email: user.email, $name: user.name, ...traits });
      // Super properties so every event (not just profiles) breaks down by role/userType.
      registerSuperProperties(traits);
      setPeoplePropertiesOnce({ firstLoginAt: new Date().toISOString() });
      maybeStartSessionRecording({ always: customer });
      track("Login");
    }
  }, [isAuthenticated, user]);

  if (error) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-4">
        <h1 className="text-2xl font-semibold">Problème d'authentification</h1>
        <p className="text-sm text-muted-foreground">{error.message}</p>
        <button
          type="button"
          onClick={() => void loginWithRedirect()}
          className="text-sm font-medium underline underline-offset-4"
        >
          Réessayer
        </button>
      </div>
    );
  }

  if (isLoading || !isAuthenticated) {
    return <FullPageLoader />;
  }

  return (
    <>
      <AnalyticsTenantSync />
      <Outlet />
    </>
  );
}
